import React, { useCallback } from "react";
import InputRange, { InputRangeProps } from "react-input-range";
import 'react-input-range/lib/css/index.css';
import "./ResizableRangeSlider.scss";

interface ResizableRangeSliderProps extends Omit<InputRangeProps, "value" | "onChange"> {
    value: { max: number; min: number };
    onChange: (value: { max: number; min: number }) => void;
    resizingRef?: React.MutableRefObject<boolean>;
    disabled: boolean;
    draggable?: boolean;
}

const defaultSliderProps: Partial<InputRangeProps> = {
    step: 1,
    draggableTrack: true,
};

const ResizableRangeSlider: React.FC<ResizableRangeSliderProps> = ({
    resizingRef,
    disabled,
    value,
    onChange,
    minValue,
    maxValue,
    draggable,
    ...rest
}) => {
    const isDraggingRef = React.useRef(false);

    const handleChange = (newValue: { max: number; min: number }) => {
        // Only trigger onChange if user is actively dragging/resizing, not on click
        if (isDraggingRef.current) {
            onChange(newValue);
        }
    };

    const handleDragResize = useCallback(() => {
        isDraggingRef.current = false;
        if (resizingRef && resizingRef.current) {
            resizingRef.current = false;
        }
    }, [resizingRef]);

    return (
        <div
            className="input__range__container"
            onMouseDown={() => {
                isDraggingRef.current = true;
                resizingRef && (resizingRef.current = true);
            }}
            onMouseUp={() => {
                handleDragResize();
            }}
            onMouseLeave={() => {
                handleDragResize();
            }}
        >
            <InputRange
                {...defaultSliderProps}
                {...rest}
                disabled={disabled}
                minValue={minValue}
                maxValue={maxValue}
                value={value}
                draggableTrack={draggable}
                onChange={(newValue) => {
                    handleChange(newValue as { max: number; min: number });
                }}
            />
        </div>
    );
};

export default ResizableRangeSlider;
